// src/admin/ContactMessages.js
import React, { useState, useEffect, useRef } from 'react';
import {
  collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Mail, Trash2, X, Search, CheckCircle, Circle,
  Phone, Volume2, VolumeX
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const lastMessageCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  const fetchMessages = async () => {
    try {
      const q = query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (!isFirstLoadRef.current && data.length > lastMessageCountRef.current) {
        if (soundEnabled) playNotificationSound();
        showBrowserNotification('New Contact Message!', 'You have a new message from a customer.');
      }

      lastMessageCountRef.current = data.length;
      isFirstLoadRef.current = false;
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [soundEnabled]);

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      setTimeout(() => {
        const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
        const osc2 = ctx2.createOscillator();
        const gain2 = ctx2.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx2.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, ctx2.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx2.currentTime + 0.5);
        osc2.start(ctx2.currentTime);
        osc2.stop(ctx2.currentTime + 0.5);
      }, 200);
    } catch (error) {
      console.error('Sound play failed:', error);
    }
  };

  const showBrowserNotification = (title, body) => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(title, { body, icon: '/favicon.ico' });
        }
      });
    }
  };

  const testSound = () => {
    playNotificationSound();
    setTimeout(() => {
      showBrowserNotification('Test Notification', 'Agar ye dikh raha hai to notification kaam kar raha hai!');
    }, 300);
  };

  const markAsRead = async (message) => {
    try {
      await updateDoc(doc(db, 'contactMessages', message.id), { read: true });
      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, read: true } : m));
      if (selectedMessage?.id === message.id) {
        setSelectedMessage({ ...selectedMessage, read: true });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const markAsUnread = async (message) => {
    try {
      await updateDoc(doc(db, 'contactMessages', message.id), { read: false });
      setMessages(prev => prev.map(m => m.id === message.id ? { ...m, read: false } : m));
      if (selectedMessage?.id === message.id) {
        setSelectedMessage({ ...selectedMessage, read: false });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (message) => {
    if (!window.confirm(`Delete message from "${message.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'contactMessages', message.id));
      if (selectedMessage?.id === message.id) setSelectedMessage(null);
      setSuccessMessage('Message deleted!');
      setShowSuccess(true);
      fetchMessages();
    } catch (error) {
      alert('Failed to delete.');
    }
  };

  const getFilteredMessages = () => {
    let filtered = [...messages];
    if (filter === 'unread') filtered = filtered.filter(m => !m.read);
    if (filter === 'read') filtered = filtered.filter(m => m.read);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(m =>
        m.name?.toLowerCase().includes(term) ||
        m.email?.toLowerCase().includes(term) ||
        m.message?.toLowerCase().includes(term) ||
        m.subject?.toLowerCase().includes(term)
      );
    }
    return filtered;
  };

  const filteredMessages = getFilteredMessages();
  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      const now = new Date();
      const diff = Math.floor((now - d) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hour(s) ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)} day(s) ago`;
      return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return '-'; }
  };

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Mail className="text-green-700" size={26} /> Contact Messages
            {unreadCount > 0 && (
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-500 text-white animate-pulse">
                {unreadCount} NEW
              </span>
            )}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Customers ke messages yahan dekhein. Naya message aaye to sound bajegi.
          </p>
        </div>

        <div className="flex gap-2">
          <button onClick={testSound}
            className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2.5 rounded-lg transition flex items-center gap-2 font-semibold border border-blue-200 text-sm">
            <Volume2 size={16} /> Test Sound
          </button>
          <button onClick={() => setSoundEnabled(!soundEnabled)}
            className={`px-4 py-2.5 rounded-lg transition flex items-center gap-2 font-semibold text-sm border ${
              soundEnabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'
            }`}>
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            {soundEnabled ? 'Sound ON' : 'Sound OFF'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: messages.length },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'read', label: 'Read', count: messages.length - unreadCount },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                filter === tab.key ? 'bg-green-700 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        <div className="relative">
          <input type="text" placeholder="Search messages..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading messages...</p>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Mail size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No messages found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMessages.map(msg => (
            <div
              key={msg.id}
              onClick={() => { setSelectedMessage(msg); if (!msg.read) markAsRead(msg); }}
              className={`relative bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition cursor-pointer ${
                !msg.read ? 'border-l-4 border-red-500' : 'border border-gray-100'
              }`}
            >
              {!msg.read && (
                <div className="absolute top-3 right-3">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                </div>
              )}

              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                  style={{ backgroundColor: '#16a34a' }}>
                  {msg.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm truncate ${!msg.read ? 'text-gray-900' : 'text-gray-700'}`}>
                    {msg.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                    <Mail size={10} /> {msg.email}
                  </p>
                </div>
              </div>

              {msg.subject && (
                <p className={`text-sm font-semibold mb-1 line-clamp-1 ${!msg.read ? 'text-gray-900' : 'text-gray-700'}`}>
                  {msg.subject}
                </p>
              )}

              <p className="text-xs text-gray-600 line-clamp-3 mb-3">
                {msg.message}
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <p className="text-[10px] text-gray-400">{formatDate(msg.createdAt)}</p>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); msg.read ? markAsUnread(msg) : markAsRead(msg); }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                    {msg.read ? <Circle size={14} /> : <CheckCircle size={14} />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(msg); }}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">Message Details</h2>
              <button onClick={() => setSelectedMessage(null)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: '#16a34a' }}>
                  {selectedMessage.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-gray-800">{selectedMessage.name}</p>
                  <p className="text-xs text-gray-500">{formatDate(selectedMessage.createdAt)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                  <Mail size={16} className="text-green-700" />
                  <a href={`mailto:${selectedMessage.email}`} className="text-sm text-blue-600 hover:underline break-all">
                    {selectedMessage.email}
                  </a>
                </div>
                {selectedMessage.phone && (
                  <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                    <Phone size={16} className="text-green-700" />
                    <a href={`tel:${selectedMessage.phone}`} className="text-sm text-blue-600 hover:underline">
                      {selectedMessage.phone}
                    </a>
                  </div>
                )}
              </div>

              {selectedMessage.subject && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">SUBJECT</p>
                  <p className="font-semibold text-gray-800">{selectedMessage.subject}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">MESSAGE</p>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t">
                <a href={`mailto:${selectedMessage.email}`}
                  className="flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition text-center flex items-center justify-center gap-2 text-sm">
                  <Mail size={16} /> Reply via Email
                </a>
                <button onClick={() => handleDelete(selectedMessage)}
                  className="px-4 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition font-semibold text-sm flex items-center gap-2">
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default ContactMessages;